<script setup>
import { computed, ref, watch } from 'vue'
import { Plus } from '@lucide/vue'
import { Badge, Button, FormField, Input, Modal, Section, SegmentedControl, Select, Table, Textarea, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveTtsVoice } from '../../api/model-capabilities.js'

const props = defineProps({
  catalog: { type: Object, required: true },
})

const emit = defineEmits(['changed'])
const { t } = useI18n()
const toast = useToast()
const saving = ref(false)
const modalOpen = ref(false)
const editingVoice = ref(null)
const form = ref(emptyVoiceForm())

const providersById = computed(() => new Map(props.catalog.providers.map(row => [row.id, row])))
const modelsById = computed(() => new Map(props.catalog.tts.models.map(row => [row.id, row])))
const providerOptions = computed(() => props.catalog.tts.provider_capabilities
  .filter(provider => props.catalog.tts.models.some(model => model.provider_capability_id === provider.id))
  .map(providerCapability => {
    const provider = providersById.value.get(providerCapability.provider_id)
    return {
      value: providerCapability.id,
      label: `${provider.display_name} · ${provider.code}`,
    }
  }))
const canAddVoice = computed(() => providerOptions.value.length > 0)
const modelOptions = computed(() => props.catalog.tts.models
  .filter(row => row.provider_capability_id === form.value.provider_capability_id)
  .map(row => ({ value: row.id, label: row.model })))
const statusOptions = computed(() => [
  { value: 'enabled', label: t('visualGameAdmin.llm.capabilities.status.enabled') },
  { value: 'disabled', label: t('visualGameAdmin.llm.capabilities.status.disabled') },
])
const voiceTypeOptions = computed(() => [
  { value: 'clone', label: t('visualGameAdmin.llm.capabilities.voiceTypes.clone') },
  { value: 'native', label: t('visualGameAdmin.llm.capabilities.voiceTypes.native') },
])
const genderOptions = computed(() => ['feminine', 'masculine', 'neutral'].map(value => ({
  value,
  label: t(`visualGameAdmin.llm.capabilities.voiceProfile.genderOptions.${value}`),
})))
const ageOptions = computed(() => ['young_adult', 'adult', 'mature', 'senior'].map(value => ({
  value,
  label: t(`visualGameAdmin.llm.capabilities.voiceProfile.ageOptions.${value}`),
})))
const status = computed({
  get: () => form.value.enabled ? 'enabled' : 'disabled',
  set: value => { form.value.enabled = value === 'enabled' },
})
const columns = computed(() => [
  { key: 'display_name', label: t('visualGameAdmin.llm.capabilities.voices.name'), width: '180px' },
  { key: 'voice_id', label: t('visualGameAdmin.llm.capabilities.voices.id'), width: '190px', mono: true },
  { key: 'voice_type', label: t('visualGameAdmin.llm.capabilities.voices.type'), width: '110px' },
  { key: 'language_code', label: t('visualGameAdmin.llm.capabilities.voiceProfile.language'), width: '100px' },
  { key: 'profile', label: t('visualGameAdmin.llm.capabilities.voiceProfile.profile'), width: '220px' },
  { key: 'model', label: t('visualGameAdmin.llm.capabilities.table.model'), width: '200px', mono: true },
  { key: 'enabled', label: t('visualGameAdmin.llm.capabilities.table.status'), width: '100px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '120px', align: 'right' },
])

watch(() => form.value.provider_capability_id, () => {
  if (modelOptions.value.some(option => option.value === form.value.model_id)) return
  form.value.model_id = modelOptions.value[0].value
  form.value.expressive_model_id = modelOptions.value[0].value
})

function emptyVoiceForm() {
  return {
    voice_id: '',
    display_name: '',
    voice_type: 'clone',
    provider_capability_id: '',
    model_id: '',
    resource_id: '',
    expressive_model_id: null,
    language_code: 'zh-CN',
    gender_presentation: 'neutral',
    age_group: 'adult',
    style_tags: [],
    style_tags_text: '',
    description: '',
    enabled: true,
  }
}

function openNew() {
  editingVoice.value = null
  form.value = emptyVoiceForm()
  form.value.provider_capability_id = providerOptions.value[0].value
  modalOpen.value = true
}

function openEdit(row) {
  editingVoice.value = row
  form.value = { ...row, enabled: row.enabled === 1, style_tags_text: row.style_tags.join('、') }
  modalOpen.value = true
}

async function submit() {
  saving.value = true
  try {
    const { style_tags_text, ...voice } = form.value
    const catalog = await saveTtsVoice({
      ...voice,
      style_tags: style_tags_text.split(/[、,，]/).map(value => value.trim()).filter(Boolean),
      expressive_model_id: form.value.voice_type === 'native' ? form.value.expressive_model_id : null,
    })
    modalOpen.value = false
    emit('changed', catalog)
    toast.success(t('visualGameAdmin.llm.capabilities.saved'))
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Section :title="t('visualGameAdmin.llm.capabilities.voices.title')" :padded="false">
    <template #actions>
      <Button
        variant="primary"
        size="sm"
        :disabled="!canAddVoice"
        :title="canAddVoice ? '' : t('visualGameAdmin.llm.capabilities.addVoiceUnavailable')"
        @click="openNew"
      >
        <Plus :size="16" />
        {{ t('visualGameAdmin.llm.capabilities.voices.add') }}
      </Button>
    </template>
    <Table :columns="columns" :rows="catalog.tts.voices">
      <template #voice_type="{ row }">{{ t(`visualGameAdmin.llm.capabilities.voiceTypes.${row.voice_type}`) }}</template>
      <template #profile="{ row }">
        {{ t(`visualGameAdmin.llm.capabilities.voiceProfile.genderOptions.${row.gender_presentation}`) }} ·
        {{ t(`visualGameAdmin.llm.capabilities.voiceProfile.ageOptions.${row.age_group}`) }} ·
        {{ row.style_tags.join(' / ') }}
      </template>
      <template #model="{ row }">{{ modelsById.get(row.model_id).model }}</template>
      <template #enabled="{ row }">
        <Badge :variant="row.enabled ? 'success' : 'neutral'">
          {{ t(`visualGameAdmin.llm.capabilities.status.${row.enabled ? 'enabled' : 'disabled'}`) }}
        </Badge>
      </template>
      <template #actions="{ row }">
        <Button variant="ghost" size="sm" @click="openEdit(row)">
          {{ t('visualGameAdmin.llm.capabilities.edit') }}
        </Button>
      </template>
    </Table>
  </Section>

  <Modal
    :open="modalOpen"
    :title="t(`visualGameAdmin.llm.capabilities.voiceForm.${editingVoice ? 'edit' : 'create'}`)"
    @update:open="modalOpen = $event"
  >
    <form class="tts-voice-form" @submit.prevent="submit">
      <FormField :label="t('visualGameAdmin.llm.capabilities.table.provider')" required>
        <Select
          v-model="form.provider_capability_id"
          :options="providerOptions"
          :disabled="editingVoice !== null"
          block
        />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voices.name')" required>
        <Input v-model="form.display_name" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voices.id')" required>
        <Input v-model="form.voice_id" :disabled="editingVoice !== null" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voices.type')">
        <Select v-model="form.voice_type" :options="voiceTypeOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voiceProfile.language')" required>
        <Input v-model="form.language_code" placeholder="zh-CN" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voiceProfile.gender')" required>
        <Select v-model="form.gender_presentation" :options="genderOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voiceProfile.age')" required>
        <Select v-model="form.age_group" :options="ageOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voiceProfile.styles')" required>
        <Input v-model="form.style_tags_text" :placeholder="t('visualGameAdmin.llm.capabilities.voiceProfile.stylesHint')" />
      </FormField>
      <FormField class="tts-voice-form__wide" :label="t('visualGameAdmin.llm.capabilities.voiceProfile.description')" required>
        <Textarea v-model="form.description" :rows="3" :maxlength="500" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.table.status')">
        <SegmentedControl v-model="status" :options="statusOptions" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voices.model')" required>
        <Select v-model="form.model_id" :options="modelOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.voices.resource')" required>
        <Input v-model="form.resource_id" />
      </FormField>
      <FormField
        v-if="form.voice_type === 'native'"
        :label="t('visualGameAdmin.llm.capabilities.voices.expressiveModel')"
        required
      >
        <Select v-model="form.expressive_model_id" :options="modelOptions" block />
      </FormField>
    </form>
    <template #footer>
      <Button variant="ghost" @click="modalOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button variant="primary" :loading="saving" @click="submit">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.tts-voice-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-lg);
  min-width: min(520px, 76vw);
}

.tts-voice-form__wide { grid-column: 1 / -1; }

@media (max-width: 760px) {
  .tts-voice-form {
    grid-template-columns: 1fr;
    min-width: 0;
  }
}
</style>
